import _omit from 'lodash/omit';
import _isUndefined from 'lodash/isUndefined';
import {CLEnumKeys, CLEnumsMapper} from 'common/src/modules/enums/enums.types';
import {
  Campaign,
  CampaignSignal,
  CampaignsStepsInformation,
  FCCampaign,
  FCServerCampaign,
  RemoteCampaignIntegration,
  RemoteCampaignSignal,
  RemoteCampaignStepsInformation,
  ServerCampaign,
  ServerSlimCampaign,
  SlimCampaign,
} from './campaigns.types';
import {CampaignIntegration} from '../campaignIntegrations/campaignIntegrations.types';
import {ConditionCategory} from '../conditions/conditions.types';
import {TaxonomyMapping, TaxonomyType} from '../taxonomy/taxonomy.types';
import taxonomize from '../taxonomy/utils/taxonomize';
import {MacroItem} from '../macros/macros.types';
import {DateTime} from 'luxon';
import ObjectStatus from '../enums/static/ObjectStatus';
import {CategorizedSignal} from '../compoundConditions/compoundConditions.types';
import {isSocialCampaign} from './utils/isSocialCampaign';
import {CampaignStep} from '../campaignWorkflow/campaignWorkflow.enums';
import {campaignStepsInformationInitialValues} from './campaigns.consts';
import {CAMPAIGN_NODE_ID} from 'common/src/components/CLCardsMap/CLCardsMap.config';
import {DTNodeToClientModel} from './utils/DTNodeToClientModel';
import {
  ServerCampaignVersion,
  DTVersion,
  ServerDecisionTree,
  CampaignWithDT,
  DTNode,
} from '../decisionTree/decisionTree.types';
import {RemoteCampaignMiniBrief, CampaignMiniBrief} from './campaignBrief/campaignBrief.types';
import _map from 'lodash/map';

const taxonomyByConditionCategory: Partial<Record<ConditionCategory, TaxonomyType>> = {
  [ConditionCategory.Product]: TaxonomyType.Product,
  [ConditionCategory.Location]: TaxonomyType.Location,
  [ConditionCategory.Content]: TaxonomyType.Content,
  [ConditionCategory.User]: TaxonomyType.FirstParty,
};

export function toLocalSlimCampaign(campaign: ServerSlimCampaign): SlimCampaign {
  return {
    ...campaign,
    ...(campaign.createTime && {createTime: DateTime.fromISO(campaign.createTime)}),
    isActive: campaign.status === ObjectStatus.Active,
    isSocial: isSocialCampaign(campaign.type),
    versionData: !!campaign.versionData
      ? {
          ...campaign.versionData,
          activationTime: {
            start: campaign.versionData.activationStart
              ? DateTime.fromISO(campaign.versionData.activationStart)
              : null,
          },
        }
      : null,
  };
}

function toClientCampaign(campaign: ServerCampaign) {
  return {
    ...campaign,
    isActive: campaign.status === ObjectStatus.Active,
    isSocial: isSocialCampaign(campaign.type),
  };
}

function toServerCampaign(campaign: Partial<Campaign>) {
  const serverCampaign = {
    ...campaign,
    ...(!_isUndefined(campaign.isActive) && {
      status: campaign.isActive ? ObjectStatus.Active : ObjectStatus.NotActive,
    }),
  };

  return _omit(serverCampaign, ['isActive']);
}

function toClientFCCampaign(campaign: FCServerCampaign): FCCampaign {
  return {
    ...campaign,
    ...toClientCampaign(campaign),
  };
}

function toClientCampaignIntegration(
  remote: RemoteCampaignIntegration,
  socialEnums: Record<string, CLEnumsMapper>,
): CampaignIntegration {
  return {
    id: remote.id,
    platform: remote.platform,
    accountId: remote.extAccountId ?? '',
    accountName: remote.extAccountName ?? '',
    campaignId: remote.extCampaignId ?? '',
    campaignName: remote.extCampaignName ?? '',
    platformName: socialEnums[CLEnumKeys.Social.Platform].byValue[remote.platform].name,
    isActive: remote.status > 0,
    status: remote.status,
    clientIntegrationId: remote.clientIntegrationId,
    platformUiName: socialEnums[CLEnumKeys.Social.Platform].byValue[remote.platform].uiName,
    data: remote.data,
    dspType: remote.dspType,
  };
}

function toClientMacroItems(
  items: MacroItem[],
  taxonomy: TaxonomyMapping,
  category: ConditionCategory,
): MacroItem[] {
  return items
    .map((signal) => ({
      ...signal,
      name: signal.taxonomyKey
        ? taxonomize(taxonomy[taxonomyByConditionCategory[category]], signal.taxonomyKey, null)
        : signal.name,
    }))
    .filter((signal) => !!signal.name);
}

function toClientConditions(
  conditions: CategorizedSignal[],
  taxonomy: TaxonomyMapping,
): CategorizedSignal[] {
  return conditions.map((condition) => ({
    ...condition,
    signals: toClientMacroItems(condition.signals, taxonomy, condition.categories[0]),
    subCategories: toClientConditions(condition.subCategories || [], taxonomy),
  }));
}

function toLocalCampaignSignal(signal: RemoteCampaignSignal): CampaignSignal {
  return {
    ...signal,
    category: signal.category as TaxonomyType,
  };
}

function toClientCampaignStepsInformation(
  remote: RemoteCampaignStepsInformation,
): CampaignsStepsInformation {
  return {
    ...campaignStepsInformationInitialValues,
    [CampaignStep.Placements]: {
      issues: remote.adSetsNumOfIssues,
      comments: 0,
    },
    [CampaignStep.ConceptPlan]: {
      issues: 0,
      comments: remote.cpNumOfComments,
    },
    [CampaignStep.CampaignLogic]: {
      issues: remote.dtNumOfIssues,
      comments: remote.dtNumOfComments,
    },
    [CampaignStep.AdsVariations]: {
      issues: remote.numOfFailedAds,
      comments: 0,
    },
  };
}

function dtVersionToClientModel({
  number,
  lastUpdate,
  createdDate,
  savedDate,
  name,
  activationStart,
  activationEnd,
  ...common
}: ServerCampaignVersion): DTVersion {
  return {
    ...common,
    name: name || `V${number}`,
    lastUpdate: DateTime.fromISO(lastUpdate),
    createdDate: DateTime.fromISO(createdDate),
    savedDate: DateTime.fromISO(savedDate),
    number: number,
    activationTime: {
      start: activationStart ? DateTime.fromISO(activationStart) : null,
      end: activationEnd ? DateTime.fromISO(activationEnd) : null,
    },
  };
}

function dtToClientModel({
  generalEnums,
  campaignEnums,
  decisionTree,
  relatedEnums,
  version,
}: {
  decisionTree: ServerDecisionTree;
  version?: ServerCampaignVersion;
  campaignEnums: Record<string, CLEnumsMapper>;
  generalEnums: Record<string, CLEnumsMapper>;
  relatedEnums: Record<string, CLEnumsMapper>;
}): CampaignWithDT {
  const campaign = toClientFCCampaign(decisionTree.campaignNode.campaign);
  const loadedVersion = version ? dtVersionToClientModel(version) : null;

  const nodesOrder = [..._map(decisionTree.nodes, 'id'), CAMPAIGN_NODE_ID];

  const nodesFromDB: DTNode[] = _map(decisionTree.nodes, (node) => {
    return DTNodeToClientModel(node, campaignEnums, generalEnums, relatedEnums);
  });

  const campaignNode = {
    x: decisionTree.campaignNode.x,
    y: decisionTree.campaignNode.y,
  };

  return {
    campaign,
    nodes: nodesFromDB,
    campaignNode,
    nodesOrder,
    loadedVersion,
    rootNodeId: decisionTree.rootNodeId,
    nextNodeId: decisionTree.nextNodeId,
    fallbackCreativeNodeId: decisionTree.fallbackCreativeNodeId,
  };
}

function toClientCampaignMiniBrief(remoteBrief: RemoteCampaignMiniBrief): CampaignMiniBrief {
  return {
    ...remoteBrief,
    startDate: remoteBrief.startDate ? DateTime.fromISO(remoteBrief.startDate) : null,
    endDate: remoteBrief.endDate ? DateTime.fromISO(remoteBrief.endDate) : null,
    audiences: _map(remoteBrief.audiences ?? [], (audience) => JSON.parse(audience)),
  };
}

function toServerCampaignMiniBrief(brief: CampaignMiniBrief) {
  return {
    ...brief,
    audiences: brief.audiences ? _map(brief.audiences, (audience) => JSON.stringify(audience)) : [],
  };
}

export {
  toClientCampaign,
  toClientCampaignIntegration,
  toServerCampaign,
  toClientConditions,
  toClientMacroItems,
  toClientFCCampaign,
  toLocalCampaignSignal,
  toClientCampaignStepsInformation,
  toClientCampaignMiniBrief,
  toServerCampaignMiniBrief,
  dtVersionToClientModel,
  dtToClientModel,
};
